import { showNotification } from "@libs/notistack/notificationStack";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";

export type FileType =
	| "images"
	| "documents"
	| "imagesAndDocuments"
	| "imagesAndPDF"
	| "courseDocuments"
	| "courseDocumentsWithImages"
	| "courseDocumentsWithImagesAndPDFAndDocs"
	| "allDocuments";

export interface FileUploadConfig {
	maxSizeInMB: number;
	allowedTypes: FileType;
}

export interface FileValidationResult {
	isValid: boolean;
	error?: string;
}

export interface FileUploadPayload {
	name: string;
	contentType: string;
	maxSizeInMB: number;
	allowedMimeTypes: Record<string, string[]>;
}

// MIME type mappings
const MIME_TYPE_MAP: Record<string, string[]> = {
	// Images
	"image/jpeg": [".jpeg", ".jpg"],
	"image/jpg": [".jpg"],
	"image/png": [".png"],
	"image/gif": [".gif"],
	"image/webp": [".webp"],
	"image/bmp": [".bmp"],

	// Documents
	"application/pdf": [".pdf"],
	"application/msword": [".doc"],
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
	"application/vnd.ms-excel": [".xls"],
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
	"application/vnd.ms-powerpoint": [".ppt"],
	"application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],

	// Text files
	"text/plain": [".txt"],
	"text/csv": [".csv"],
	"application/json": [".json"],
	"application/xml": [".xml"],

	// Archives
	"application/zip": [".zip"],
	"application/x-rar-compressed": [".rar"],
	"application/x-7z-compressed": [".7z"],
};

// Define allowed MIME types by category
const ALLOWED_MIME_TYPES: Record<FileType, string[]> = {
	images: ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "image/bmp"],
	documents: ["application/pdf"],
	imagesAndDocuments: [
		// All images
		"image/jpeg",
		"image/jpg",
		"image/png",
		"image/gif",
		"image/webp",
		"image/bmp",
		// Common documents
		"application/pdf",
	],
	imagesAndPDF: [
		// JPEG images
		"image/jpeg",
		"image/jpg",
		"image/png",
		"image/gif",
		"image/webp",
		// PDF only (no BMP)
		"application/pdf",
	],
	courseDocuments: [
		// PDF documents
		"application/pdf",
		// Word documents
		"application/msword",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		// Excel documents
		"application/vnd.ms-excel",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	],
	courseDocumentsWithImages: [
		// PDF documents
		"application/pdf",
		// Word documents
		"application/msword",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		// Excel documents
		"application/vnd.ms-excel",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		// Images
		"image/jpeg",
		"image/jpg",
		"image/png",
	],
	courseDocumentsWithImagesAndPDFAndDocs: [
		// PDF documents
		"application/pdf",
		// Word documents
		"application/msword",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		// Images
		"image/jpeg",
		"image/jpg",
		"image/png",
		"image/bmp",
		"image/gif",
		"image/webp",
	],
	allDocuments: [
		// PDF documents
		"application/pdf",
		// Word documents
		"application/msword",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		// Excel documents
		"application/vnd.ms-excel",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		// PowerPoint documents
		"application/vnd.ms-powerpoint",
		"application/vnd.openxmlformats-officedocument.presentationml.presentation",
		// Text files
		"text/plain",
		"text/csv",
		"application/json",
		"application/xml",
		// Archives
		"application/zip",
		"application/x-rar-compressed",
		"application/x-7z-compressed",
	],
};

export interface ValidateFileOptions {
	/** When false, errors are returned only in the result (no snackbar). Default true. */
	notify?: boolean;
}

/**
 * Validate file type and size based on configuration
 * @param file - The file to validate
 * @param config - Configuration object with max size and allowed types
 * @returns Validation result with isValid flag and error message
 */
export const validateFile = (
	file: File,
	config: FileUploadConfig,
	options?: ValidateFileOptions,
): FileValidationResult => {
	const { maxSizeInMB, allowedTypes } = config;
	const notify = options?.notify !== false;

	// Check file size
	const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
	if (file.size > maxSizeInBytes) {
		const error = `File size exceeds ${maxSizeInMB}MB limit. Please choose a smaller file. Current size: ${(
			file.size /
			(1024 * 1024)
		).toFixed(2)}MB`;
		if (notify) {
			showNotification({
				message: error,
				severity: SNACKBAR_SEVERITY.ERROR,
			});
		}
		return { isValid: false, error };
	}

	// Check if file is SVG (not allowed)
	if (file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg")) {
		const error = "SVG files are not allowed. Please upload a different file format.";
		if (notify) {
			showNotification({
				message: error,
				severity: SNACKBAR_SEVERITY.ERROR,
			});
		}
		return { isValid: false, error };
	}

	// Get allowed MIME types based on category
	const allowedMimeTypes = ALLOWED_MIME_TYPES[allowedTypes];

	// Check file type
	if (!allowedMimeTypes.includes(file.type)) {
		const allowedExtensions = allowedMimeTypes
			.map(mimeType => MIME_TYPE_MAP[mimeType] || [])
			.flat()
			.map(ext => ext.toUpperCase())
			.filter((v, i, a) => a.indexOf(v) === i) // Remove duplicates
			.sort();

		let errorMessage = `Invalid file type. Please upload `;

		if (allowedTypes === "images") {
			errorMessage += "image files only";
		} else if (allowedTypes === "documents") {
			errorMessage += "PDF files only";
		} else if (allowedTypes === "imagesAndDocuments") {
			errorMessage += "PDF or image files only";
		} else if (allowedTypes === "imagesAndPDF") {
			errorMessage += "PDF or image files only (JPEG, PNG, GIF, WebP, PDF)";
		} else if (allowedTypes === "courseDocuments") {
			errorMessage += "document files only";
		} else if (allowedTypes === "courseDocumentsWithImages") {
			errorMessage += "document or image files only (PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG)";
		} else if (allowedTypes === "courseDocumentsWithImagesAndPDFAndDocs") {
			errorMessage += "document or image files only (PDF, DOC, DOCX, JPG, JPEG, PNG, BMP, GIF, WEBP)";
		} else if (allowedTypes === "allDocuments") {
			errorMessage += "document files only";
		}

		errorMessage += ` (${allowedExtensions.join(", ")})`;

		if (notify) {
			showNotification({
				message: errorMessage,
				severity: SNACKBAR_SEVERITY.ERROR,
			});
		}
		return { isValid: false, error: errorMessage };
	}

	return { isValid: true };
};

/**
 * Generate the upload payload for the API
 * @param file - The file to generate payload for
 * @param config - Configuration object with max size and allowed types
 * @returns API payload with file info and validation rules
 */
export const generateUploadPayload = (file: File, config: FileUploadConfig): FileUploadPayload => {
	const { maxSizeInMB, allowedTypes } = config;

	// Get allowed MIME types based on category
	const allowedMimeTypes = ALLOWED_MIME_TYPES[allowedTypes];

	// Build the allowedMimeTypes object for the API
	const allowedMimeTypesObject: Record<string, string[]> = {};
	allowedMimeTypes.forEach(mimeType => {
		const extensions = MIME_TYPE_MAP[mimeType];
		if (extensions && extensions.length > 0) {
			allowedMimeTypesObject[mimeType] = extensions;
		}
	});

	return {
		name: file.name,
		contentType: file.type,
		maxSizeInMB,
		allowedMimeTypes: allowedMimeTypesObject,
	};
};

/**
 * Get the HTML accept attribute string for file input
 * @param allowedTypes - The allowed file type category
 * @returns String to use in accept attribute
 */
export const getFileInputAccept = (allowedTypes: FileType): string => {
	if (allowedTypes === "images") {
		return "image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp,.jpg,.jpeg,.png,.gif,.webp,.bmp";
	}
	if (allowedTypes === "documents") {
		return "application/pdf,.pdf";
	}
	if (allowedTypes === "imagesAndDocuments") {
		return "image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf,.jpg,.jpeg,.png,.gif,.webp,.pdf";
	}
	if (allowedTypes === "imagesAndPDF") {
		return "image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf,.jpg,.jpeg,.png,.gif,.webp,.pdf";
	}
	if (allowedTypes === "courseDocuments") {
		return "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.pdf,.doc,.docx,.xls,.xlsx";
	}
	if (allowedTypes === "courseDocumentsWithImages") {
		return "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/jpg,image/png,.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png";
	}
	if (allowedTypes === "courseDocumentsWithImagesAndPDFAndDocs") {
		return "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/jpg,image/png,image/bmp,image/gif,image/webp,.pdf,.doc,.docx,.jpg,.jpeg,.png,.bmp,.gif,.webp";
	}
	if (allowedTypes === "allDocuments") {
		return "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,application/json,application/xml,application/zip,application/x-rar-compressed,application/x-7z-compressed,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.json,.xml,.zip,.rar,.7z";
	}
	return "*";
};

/**
 * Get human-readable format description for display
 * @param allowedTypes - The allowed file type category
 * @returns Array of format names
 */
export const getFormatsDisplay = (allowedTypes: FileType): string[] => {
	if (allowedTypes === "images") {
		return ["JPG", "PNG", "GIF", "BMP", "WebP"];
	}
	if (allowedTypes === "documents") {
		return ["PDF"];
	}
	if (allowedTypes === "imagesAndDocuments") {
		return ["PDF", "JPEG", "PNG", "GIF", "WebP"];
	}
	if (allowedTypes === "imagesAndPDF") {
		return ["PDF", "JPEG", "PNG", "GIF", "WebP"];
	}
	if (allowedTypes === "courseDocuments") {
		return ["PDF", "DOC", "DOCX", "XLS", "XLSX"];
	}
	if (allowedTypes === "courseDocumentsWithImages") {
		return ["PDF", "DOC", "DOCX", "XLS", "XLSX", "JPG", "JPEG", "PNG"];
	}
	if (allowedTypes === "courseDocumentsWithImagesAndPDFAndDocs") {
		return ["PDF", "DOC", "DOCX", "JPG", "JPEG", "PNG", "BMP", "GIF", "WebP"];
	}
	if (allowedTypes === "allDocuments") {
		return ["PDF", "DOC", "DOCX", "XLS", "XLSX", "PPT", "PPTX", "TXT", "CSV", "JSON", "XML", "ZIP", "RAR", "7Z"];
	}
	return [];
};

/**
 * Common configurations for different use cases
 */
export const FILE_UPLOAD_CONFIGS = {
	// Profile pictures - images only, 1MB max
	PROFILE_PICTURE: {
		maxSizeInMB: 1,
		allowedTypes: "images" as FileType,
	},

	// General attachments - images and PDF, 2MB max
	GENERAL_ATTACHMENT: {
		maxSizeInMB: 2,
		allowedTypes: "imagesAndDocuments" as FileType,
	},

	// Affidavit - PDF only, 2MB max
	AFFIDAVIT: {
		maxSizeInMB: 2,
		allowedTypes: "documents" as FileType,
	},

	// Bank details - images and PDF, 5MB max
	BANK_DETAILS: {
		maxSizeInMB: 5,
		allowedTypes: "imagesAndDocuments" as FileType,
	},

	// Course documents - PDF, DOC, DOCX, XLS, XLSX, 2MB max
	COURSE_DOCUMENTS: {
		maxSizeInMB: 2,
		allowedTypes: "courseDocuments" as FileType,
	},

	// Course documents with images - PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG, 5MB max
	COURSE_DOCUMENTS_WITH_IMAGES: {
		maxSizeInMB: 5,
		allowedTypes: "courseDocumentsWithImages" as FileType,
	},

	// Course documents with images and PDF and docs - PDF, DOC, DOCX, JPG, JPEG, PNG, BMP, GIF, WEBP, 100MB max
	COURSE_DOCUMENTS_WITH_IMAGES_AND_PDF_AND_DOCS: {
		maxSizeInMB: 100,
		allowedTypes: "courseDocumentsWithImagesAndPDFAndDocs" as FileType,
	},

	// Member attachments - images and PDF, 1MB max (legacy single attachment)
	MEMBER_ATTACHMENT: {
		maxSizeInMB: 1,
		allowedTypes: "images" as FileType,
	},

	// Member documents - PDF and images, 2MB max (cv, experience letter, trade certificate)
	MEMBER_DOCUMENT: {
		maxSizeInMB: 2,
		allowedTypes: "imagesAndPDF" as FileType,
	},

	// All document types - comprehensive document support, 5MB max
	ALL_DOCUMENTS: {
		maxSizeInMB: 5,
		allowedTypes: "allDocuments" as FileType,
	},
} as const;

/**
 * Validate multiple files at once
 * @param files - Array of files to validate
 * @param config - Configuration object with max size and allowed types
 * @returns Validation result with isValid flag and error message
 */
export const validateMultipleFiles = (files: File[], config: FileUploadConfig): FileValidationResult => {
	for (const file of files) {
		const validation = validateFile(file, config);
		if (!validation.isValid) {
			return validation;
		}
	}
	return { isValid: true };
};

/**
 * Get file size in human readable format
 * @param bytes - File size in bytes
 * @returns Human readable file size
 */
export const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return "0 Bytes";
	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Get file extension from filename
 * @param filename - The filename
 * @returns File extension in uppercase
 */
export const getFileExtension = (filename: string): string => {
	return filename.split(".").pop()?.toUpperCase() || "FILE";
};
